import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Time is funny in Ruby",
  "description": "",
  "published": "2017-03-29T00:00:00.000Z",
  "redirect_from": ["/blog/time-funny-ruby/swizec/7525"],
  "categories": "Ruby Rails, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <lite-youtube {...{
      "videoid": "2uj_2G_KaGw",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`Ruby is great. Or maybe Rails is what's great… after 4 years, I still don't quite know where the line lies ?`}</p>
    <p>{`One of my favorite features is this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ruby"
      }}>{`> "next sunday".to_date
 => Sun, 26 Mar 2017
\`\`\`
\`\`\`ruby
> 2.2.0 :003 > "last sunday".to_date
 => Sun, 26 Mar 2017
`}</code></pre>
    <p>{`Wait a minute… `}<inlineCode parentName="p">{`next sunday`}</inlineCode>{` and `}<inlineCode parentName="p">{`last sunday`}</inlineCode>{` are the same date? O.o`}</p>
    <p>{`By the way, it's Tuesday right now. Next Sunday is April 2nd, and last Sunday was March 26th.`}</p>
    <p>{`Ok, that's weird. But what if we change `}<inlineCode parentName="p">{`Date.now`}</inlineCode>{` to Monday? Let's try that.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ruby"
      }}>{`2.2.0 :008 > Timecop.freeze("April 3rd, 2017".to_datetime) do
2.2.0 :009 >     Date.today
2.2.0 :010?>   end
 => Sun, 02 Apr 2017
`}</code></pre>
    <p>{`?`}</p>
    <p><em parentName="p">{`PS: `}<a parentName="em" {...{
          "href": "https://github.com/travisjeffery/timecop"
        }}>{`Timecop`}</a>{` is a Ruby library meant for testing. It lets you specify the value of "now"`}</em></p>
    <p>{`April 2nd is not April 3rd… timezones? Gotta be timezones.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ruby"
      }}>{`2.2.0 :011 > Timecop.freeze("April 3rd, 2017".to_datetime) do
2.2.0 :012 >     DateTime.now
2.2.0 :013?>   end
 => Sun, 02 Apr 2017 17:00:00 -0700
`}</code></pre>
    <p>{`Ah. `}<inlineCode parentName="p">{`to_datetime`}</inlineCode>{` without a time of day sets time to midnight in UTC. Then when you call `}<inlineCode parentName="p">{`DateTime.now`}</inlineCode>{`, you get your local timezone. Completely intuitive!`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ruby"
      }}>{`2.2.0 :014 > Timecop.freeze("April 3rd, 2017 1pm".to_datetime) do
2.2.0 :015 >     DateTime.now
2.2.0 :016?>   end
 => Mon, 03 Apr 2017 07:00:00 -0700
`}</code></pre>
    <p>{`Okay, that's better. Does `}<inlineCode parentName="p">{`"next Sunday"`}</inlineCode>{` work now?`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ruby"
      }}>{`2.2.0 :020 > Timecop.freeze("Mar 27, 2017 1pm".to_datetime) do
2.2.0 :021 >     "next sunday".to_datetime
2.2.0 :022?>   end
 => Sun, 26 Mar 2017 00:00:00 +0000
`}</code></pre>
    <p>{`Nope. Doesn't even react to current time being mocked. `}<em parentName="p">{`That`}</em>{`'s intuitive.`}</p>
    <p>{`Turns out, if you want to find next Sunday, you have to use `}<inlineCode parentName="p">{`Date.today.sunday`}</inlineCode>{`. Because… I don't know why.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ruby"
      }}>{`2.2.0 :023 > Date.today.sunday
 => Sun, 02 Apr 2017

2.2.0 :024 > Timecop.freeze("April 3rd, 2017 1pm".to_datetime) do
2.2.0 :025 >     Date.today.sunday
2.2.0 :026?>   end
 => Sun, 09 Apr 2017
`}</code></pre>
    <p>{`Excellent! That worked. Today, we get next Sunday. On Monday, we get next next Sunday. Just what you'd expect!`}</p>
    <p>{`Now watch this.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ruby"
      }}>{`2.2.0 :027 > Timecop.freeze("April 3rd, 2017".to_datetime) do
2.2.0 :028 >     Date.today.sunday
2.2.0 :029?>   end
 => Sun, 02 Apr 2017
`}</code></pre>
    <p>{`Timezones? Timezones.`}</p>
    <p>{`Even though time freezing happens in UTC, time getting happens in your local timezone. So when they misalign, Monday becomes Sunday, and `}<em parentName="p">{`obviously`}</em>{` if today is Sunday and you want to find next Sunday, that's today. D'oh.`}</p>
    <p>{`?`}</p>
    <p>{`Oh, the fun I had figuring that one out! Almost pushed to production without realizing these quirks existed! ?`}</p>
    <h2 {...{
      "id": "but-why-lets-find-out"
    }}>{`But why? Let's find out.`}</h2>
    <p>{`Here is the source for `}<inlineCode parentName="p">{`to_datetime`}</inlineCode>{`: `}<a parentName="p" {...{
        "href": "http://apidock.com/rails/String/to%5C_datetime"
      }}>{`link`}</a></p>
    <pre><code parentName="pre" {...{
        "className": "language-ruby"
      }}>{`# File activesupport/lib/active_support/core_ext/string/conversions.rb, line 53
  def to_datetime
    ::DateTime.parse(self, false) unless blank?
  end
`}</code></pre>
    <p>{`Great, we know that `}<inlineCode parentName="p">{`to_datetime`}</inlineCode>{` is a Rails method, not a Ruby method. It’s part of the `}<inlineCode parentName="p">{`ActiveSupport`}</inlineCode>{` gem, which looks like a bag of all utility methods you might find useful in a Rails project.`}</p>
    <p>{`Neat.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`to_datetime`}</inlineCode>{` method looks like magic. My understanding is that it's part of the string class, which makes it defined on every string. Once called, it passes itself – `}<inlineCode parentName="p">{`self`}</inlineCode>{` – into `}<inlineCode parentName="p">{`Datetime.parse`}</inlineCode>{`.`}</p>
    <p>{`So what is `}<inlineCode parentName="p">{`DateTime`}</inlineCode>{`?`}</p>
    <p>{`It looks like a part of core extensions. Its source is split into `}<a parentName="p" {...{
        "href": "https://github.com/rails/rails/tree/92703a9ea5d8b96f30e0b706b801c9185ef14f0e/activesupport/lib/active_support/core_ext/date_time"
      }}>{`five files`}</a>{`, so I have no idea what's what. Curiously, there is also `}<inlineCode parentName="p">{`DateAndTime`}</inlineCode>{`. They do not seem to do the same things. ?`}</p>
    <p>{`And I'm having trouble finding the `}<inlineCode parentName="p">{`parse`}</inlineCode>{` method. It shows up in `}<a parentName="p" {...{
        "href": "https://github.com/rails/rails/search?l=Ruby&q=parse&type=&utf8=%E2%9C%93"
      }}>{`107 rails source files`}</a>{` and none of them is `}<inlineCode parentName="p">{`DateTime`}</inlineCode>{`. How strange… maybe it's in Ruby after all?`}</p>
    <p>{`It is!`}</p>
    <p><a parentName="p" {...{
        "href": "https://github.com/ruby/ruby/blob/d8bf1785316c55e3befac9db0981180bd1432b49/ext/date/date_core.c"
      }}>{`Deep inside Ruby's C source code`}</a>{`, the `}<inlineCode parentName="p">{`DateTime`}</inlineCode>{` class is defined. You'd think Ruby used the `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Bootstrapping%5C_(compilers)"
      }}>{`bootstrapping compiler`}</a>{` approach and was written in Ruby, but nope. It looks like it's done in C. Who knew… ¯`}<em parentName="p">{`(ツ)`}</em>{`/¯`}</p>
    <p>{`To the best of my understanding, this is the code that becomes `}<inlineCode parentName="p">{`.parse`}</inlineCode>{` once Ruby is compiled and looks like Ruby.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-C"
      }}>{`static VALUE
datetime_s_parse(int argc, VALUE *argv, VALUE klass)
{
    VALUE str, comp, sg;

    rb_scan_args(argc, argv, "03", &str, &comp, &sg);

    switch (argc) {
      case 0:
    str = rb_str_new2("-4712-01-01T00:00:00+00:00");
      case 1:
    comp = Qtrue;
      case 2:
    sg = INT2FIX(DEFAULT_SG);
    }

    {
    VALUE argv2[2], hash;

    argv2[0] = str;
    argv2[1] = comp;
    hash = date_s__parse(2, argv2, klass);
    return dt_new_by_frags(klass, hash, sg);
    }
}
`}</code></pre>
    <p>{`I don't know what all this code does, but it seems to eventually use good old `}<inlineCode parentName="p">{`strptime`}</inlineCode>{` and guesses which format to use. That means it doesn't really care if we say `}<inlineCode parentName="p">{`this Sunday`}</inlineCode>{` or `}<inlineCode parentName="p">{`next Sunday`}</inlineCode>{`. It sees only `}<inlineCode parentName="p">{`Sunday`}</inlineCode>{`.`}</p>
    <p>{`And when `}<inlineCode parentName="p">{`strptime`}</inlineCode>{` sees `}<inlineCode parentName="p">{`Sunday`}</inlineCode>{`, it returns the current's week's Sunday, which in Ruby land is the 0th day of the week. I don't know why Ruby thinks Sunday is the first day of the week, but it seems like large parts of the world think that way.`}</p>
    <p><em parentName="p">{`shrug`}</em></p>
    <blockquote>
      <p parentName="blockquote">{`According to the Hebrew calendars and traditional Christian calendars, Sunday is the first day of the week. However, according to the International Organization for Standardization ISO 8601, Sunday is the seventh and last day of the week.`}</p>
      <p parentName="blockquote"><a parentName="p" {...{
          "href": "https://en.wikipedia.org/wiki/Sunday"
        }}>{`Wikipedia`}</a></p>
    </blockquote>
    <h2 {...{
      "id": "so"
    }}>{`So?`}</h2>
    <p>{`That was fun. I don't think we learned anything `}<em parentName="p">{`useful`}</em>{`. But it's good to know that even though Ruby and Rails can `}<em parentName="p">{`handle`}</em>{` fancy relative time strings, they actually ignore the most informative part.`}</p>
    <p>{`Oops. ??‍♂️`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      